import { useEffect, useRef } from 'react';

interface TurnstileWidgetProps {
  siteKey: string;
  resetSignal: number;
  onToken: (token: string) => void;
}

const SCRIPT_ID = 'cloudflare-turnstile-script';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export function TurnstileWidget({ siteKey, resetSignal, onToken }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let widgetId: string | undefined;
    let cancelled = false;
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;

    function renderWidget() {
      const container = containerRef.current;
      if (cancelled || !container || !window.turnstile) {
        return;
      }

      widgetId = window.turnstile.render(container, {
        sitekey: siteKey,
        action: 'contact',
        theme: 'auto',
        callback: onToken,
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken('')
      });
    }

    if (window.turnstile) {
      renderWidget();
    } else if (script) {
      script.addEventListener('load', renderWidget);
    } else {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', renderWidget);
      document.head.appendChild(script);
    }

    return () => {
      cancelled = true;
      script?.removeEventListener('load', renderWidget);
      if (widgetId && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [onToken, resetSignal, siteKey]);

  return <div ref={containerRef} aria-label="Human verification" />;
}
